import { registerComponent, debounce } from "helpers";

registerComponent(
  "predictive-search",
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      this.statusElement = this.querySelector(
        "[data-predictive-search-status]",
      );
      this.loadingText = this.statusElement.getAttribute("data-loading-text");

      if (this.input) {
        this.input.form.addEventListener("reset", this.onFormReset.bind(this));
        this.input.addEventListener(
          "input",
          debounce((event) => {
            this.onChange(event);
          }, 300).bind(this),
        );
      }
      const keywordScript = this.querySelector("script[data-keyword-triggers]");
      this.keywordTriggers = keywordScript
        ? JSON.parse(keywordScript.textContent).map((k) =>
            k.trim().toLowerCase(),
          )
        : [];
      this.firedKeywords = new Set();

      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector(
        "[data-predictive-search]",
      );
      this.allPredictiveSearchInstances =
        document.querySelectorAll("predictive-search");
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = "";

      this.setupEventListeners();
    }

    toggleResetButton() {
      const resetIsHidden = this.resetButton.classList.contains("hidden");
      if (this.input.value.length > 0 && resetIsHidden) {
        this.resetButton.classList.remove("hidden");
      } else if (this.input.value.length === 0 && !resetIsHidden) {
        this.resetButton.classList.add("hidden");
      }
    }

    setupEventListeners() {
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

      this.input.addEventListener("focus", this.onFocus.bind(this));
      this.addEventListener("focusout", this.onFocusOut.bind(this));
      this.addEventListener("keyup", this.onKeyup.bind(this));
      this.addEventListener("keydown", this.onKeydown.bind(this));
      this.addEventListener("keyword:match", this.onKeywordMatch.bind(this));
    }

    onKeywordMatch() {
      const spacing = window.innerWidth * 0.03333; // mirrors --content-wrapper-spacing: max(3.333vw, 1.6rem)
      const rect = this.getBoundingClientRect();

      // When the expanded dropdown (min-width: 1024px, left: -spacing) would overflow
      // the right edge of the viewport, shift it left so it fits and realign the keyword panel.
      if (rect.left - spacing + 1024 > window.innerWidth) {
        const dropdownLeft = window.innerWidth - spacing - 1024 - rect.left;
        const gridCol = (window.innerWidth - 2 * spacing) / 12;
        const panelLeft = window.innerWidth - spacing - 3 * gridCol - rect.left;
        this.style.setProperty("--keyword-dropdown-left", `${dropdownLeft}px`);
        this.style.setProperty("--keyword-panel-left", `${panelLeft}px`);
      }

      const video = this.querySelector("[data-peanuts-search-animation]");
      if (video) {
        video.currentTime = 0;
        video.play();
      }
      this.setAttribute("keyword-active", true);
    }

    hideKeywordPanel() {
      if (!this.hasAttribute("keyword-active")) return;
      this.removeAttribute("keyword-active");
      this.style.removeProperty("--keyword-dropdown-left");
      this.style.removeProperty("--keyword-panel-left");
      const panel = this.querySelector(".search-keyword-panel");
      if (panel) {
        panel.addEventListener(
          "transitionend",
          () => panel.querySelector("video")?.pause(),
          { once: true },
        );
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      this.toggleResetButton();
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector("#predictive-search-results-wrapper")?.remove();
      }

      // Update the term asap, don't wait for the predictive search query to finish loading
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);

      this.searchTerm = newSearchTerm;
      this.checkKeywordTriggers(newSearchTerm);
      if (this.firedKeywords.size === 0) this.hideKeywordPanel();

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    checkKeywordTriggers(term) {
      if (!this.keywordTriggers.length) return;
      const lowerTerm = term.toLowerCase();
      this.keywordTriggers.forEach((keyword) => {
        if (lowerTerm.includes(keyword)) {
          if (!this.firedKeywords.has(keyword)) {
            this.firedKeywords.add(keyword);
            this.dispatchEvent(
              new CustomEvent("keyword:match", {
                bubbles: true,
                detail: { keyword },
              }),
            );
          }
        } else {
          this.firedKeywords.delete(keyword);
        }
      });
    }

    onFormSubmit(event) {
      if (
        !this.getQuery().length ||
        this.querySelector('[aria-selected="true"] a')
      )
        event.preventDefault();
    }

    shouldResetForm() {
      return !this.querySelector('[aria-selected="true"] a');
    }

    onFormReset(event) {
      // Prevent default so the form reset doesn't set the value gotten from the url on page load
      event.preventDefault();
      // Don't reset if the user has selected an element on the predictive search dropdown
      if (this.shouldResetForm()) {
        this.input.value = "";
        this.input.focus();
        this.toggleResetButton();
        this.searchTerm = "";
        this.firedKeywords = new Set();
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
        this.hideKeywordPanel();
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute("results") === "true") {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case "ArrowUp":
          this.switchOption("up");
          break;
        case "ArrowDown":
          this.switchOption("down");
          break;
        case "Enter":
          this.selectOption();
          break;
        default:
      }
    }

    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (event.code === "ArrowUp" || event.code === "ArrowDown") {
        event.preventDefault();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector(
        "[data-predictive-search-search-for-text]",
      );
      const currentButtonText = searchForTextElement?.innerText;
      if (currentButtonText) {
        if (!previousTerm) return;
        const escapedTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const matches = currentButtonText.match(new RegExp(escapedTerm, "gi"));
        if (!matches || matches.length > 1) return;
        const newButtonText = currentButtonText.replace(
          new RegExp(escapedTerm, "i"),
          newTerm,
        );
        searchForTextElement.innerText = newButtonText;
      }
    }

    switchOption(direction) {
      if (!this.getAttribute("open")) return;

      const moveUp = direction === "up";
      const selectedElement = this.querySelector('[aria-selected="true"]');

      // Filter out hidden elements (duplicated page and article resources) thanks
      // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
      const allVisibleElements = Array.from(
        this.querySelectorAll(
          "li, button.predictive-search__search-for-button",
        ),
      ).filter((element) => element.offsetParent !== null);
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i += 1;
      }

      if (!moveUp && selectedElement) {
        activeElementIndex =
          selectedElementIndex === allVisibleElements.length - 1
            ? 0
            : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex =
          selectedElementIndex === 0
            ? allVisibleElements.length - 1
            : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute("aria-selected", true);
      if (selectedElement) selectedElement.setAttribute("aria-selected", false);

      this.input.setAttribute("aria-activedescendant", activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector(
        '[aria-selected="true"] a, button[aria-selected="true"]',
      );

      if (selectedOption) selectedOption.click();
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(" ", "-").toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      fetch(
        `${WYATT.routes.predictive_search_url}?q=${encodeURIComponent(
          searchTerm,
        )}&section_id=predictive-search`,
        {
          signal: this.abortController.signal,
        },
      )
        .then((response) => {
          let error;
          if (!response.ok) {
            error = new Error(response.status);
            this.close();
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector("#shopify-section-predictive-search").innerHTML;
          // Save bandwidth keeping the cache in all instances synced
          this.allPredictiveSearchInstances.forEach(
            (predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            },
          );
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }
          this.close();
          throw error;
        });
    }

    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
      this.setAttribute("loading", true);
    }

    setLiveRegionText(statusText) {
      this.statusElement.setAttribute("aria-hidden", "false");
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute("aria-hidden", "true");
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute("results", true);

      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      this.removeAttribute("loading");
      this.setLiveRegionText(
        this.querySelector("[data-predictive-search-live-region-count-value]")
          .textContent,
      );
    }

    getResultsMaxHeight() {
      this.resultsMaxHeight =
        window.innerHeight -
        document.querySelector(".page-header").getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }

    open() {
      this.predictiveSearchResults.style.maxHeight =
        this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute("open", true);
      this.input.setAttribute("aria-expanded", true);
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
      this.hideKeywordPanel();
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = "";
        this.removeAttribute("results");
      }
      const selected = this.querySelector('[aria-selected="true"]');

      if (selected) selected.setAttribute("aria-selected", false);

      this.input.setAttribute("aria-activedescendant", "");
      this.removeAttribute("loading");
      this.removeAttribute("open");
      this.input.setAttribute("aria-expanded", false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute("style");
    }
  },
);
